import React from 'react';
import {
  N0,
  N100,
  N40A,
  Green500,
  Red300,
  Red500,
  Yellow500,
  N400,
} from '@trello/colors';
import { formatHumanDate } from '@trello/dates';
import cx from 'classnames';
import styles from './Badge.less';

import { IconBadge } from './Badge';
import ArchivedIcon from '../icons/ArchivedIcon';
import AttachmentIcon from '../icons/AttachmentIcon';
import ChecklistIconComplete from '../icons/ChecklistIconComplete';
import ChecklistIconIncomplete from '../icons/ChecklistIconIncomplete';
import CommentIcon from '../icons/CommentIcon';
import DescriptionIcon from '../icons/DescriptionIcon';
import DueDateIconComplete from '../icons/DuedateCompleteIcon';
import DueDateIcon from '../icons/DuedateIcon';
import NotificationIcon from '../icons/NotificationIcon';
import SubscribeIcon from '../icons/SubscribeIcon';
import CardTemplateIcon from '../icons/CardTemplateIcon';
import TrelloAttachmentIcon from '../icons/TrelloAttachmentIcon';
import VoteIcon from '../icons/VoteIcon';

const TWENTY_FOUR_HOURS = 86400000;
const THIRTY_SIX_HOURS = 129600000;

/**
 * Archived Badge
 */
interface ArchivedBadgeProps {
  className?: string;
}

export const ArchivedBadge: React.FC<ArchivedBadgeProps> = ({
  className,
  children,
}) => (
  <IconBadge className={className} icon={<ArchivedIcon />}>
    {children}
  </IconBadge>
);

/**
 * Attachments Badge
 */
interface AttachmentsBadgeProps {
  className?: string;
  numAttachments?: number;
}

export const AttachmentsBadge: React.FC<AttachmentsBadgeProps> = ({
  numAttachments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<AttachmentIcon />}>
    {numAttachments}
  </IconBadge>
);

/**
 * Card Template Badge
 */

interface CardTemplateBadgeProps {
  className?: string;
}

export const CardTemplateBadge: React.FC<CardTemplateBadgeProps> = ({
  className,
  children,
}) => (
  <IconBadge
    className={cx(className, styles.isTemplate)}
    icon={<CardTemplateIcon />}
  >
    {children}
  </IconBadge>
);

/**
 * Checklist Badge
 */
interface ChecklistBadgeProps {
  className?: string;
  numItems?: number;
  numComplete?: number;
  checkItemsEarliestDue?: Date | null;
}

export const ChecklistBadge: React.FC<ChecklistBadgeProps> = ({
  numComplete = 0,
  numItems = 0,
  checkItemsEarliestDue,
  className,
}) => {
  const isComplete = numComplete === numItems;

  let bgColor = isComplete ? Green500 : undefined;
  let fontColor = isComplete ? N0 : undefined;
  const icon = isComplete ? (
    <ChecklistIconComplete />
  ) : (
    <ChecklistIconIncomplete />
  );

  let innerText = `${numComplete}/${numItems}`;

  if (checkItemsEarliestDue) {
    const now = Date.now();
    const dueDateTime = checkItemsEarliestDue.getTime();
    const isDueSoon =
      dueDateTime - TWENTY_FOUR_HOURS <= now && dueDateTime > now;
    const isDueNow = dueDateTime <= now && dueDateTime + THIRTY_SIX_HOURS > now;
    const isPastDue = dueDateTime + THIRTY_SIX_HOURS <= now;

    if (isPastDue) {
      bgColor = Red300;
      fontColor = N0;
    } else if (isDueNow) {
      bgColor = Red500;
      fontColor = N0;
    } else if (isDueSoon) {
      bgColor = Yellow500;
      fontColor = N0;
    } else if (isComplete) {
      bgColor = Green500;
      fontColor = N0;
    }

    innerText += ` • ${formatHumanDate(checkItemsEarliestDue)}`;
  }

  return (
    <IconBadge
      className={className}
      bgColor={bgColor}
      fontColor={fontColor}
      icon={icon}
    >
      {innerText}
    </IconBadge>
  );
};

/**
 * Comments Badge
 */
interface CommentsBadgeProps {
  className?: string;
  numComments?: number;
}

export const CommentsBadge: React.FC<CommentsBadgeProps> = ({
  numComments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<CommentIcon />}>
    {numComments}
  </IconBadge>
);

/**
 * Description Badge
 */
interface DescriptionBadgeProps {
  className?: string;
}

export const DescriptionBadge: React.FC<DescriptionBadgeProps> = ({
  className,
}) => <IconBadge className={className} icon={<DescriptionIcon />} />;

/**
 * DueDate Badge
 */
interface DueDateBadgeProps {
  className?: string;
  dueDate: Date;
  isComplete?: boolean;
  clickable?: boolean;
}

export const DueDateBadge: React.FC<DueDateBadgeProps> = ({
  children,
  className,
  dueDate,
  clickable = false,
  isComplete = false,
}) => {
  const now = Date.now();
  const dueDateTime = dueDate.getTime();
  const isDueSoon = dueDateTime - TWENTY_FOUR_HOURS <= now && dueDateTime > now;
  const isDueNow = dueDateTime <= now && dueDateTime + THIRTY_SIX_HOURS > now;
  const isPastDue = dueDateTime + THIRTY_SIX_HOURS <= now;

  // If the badge is clickable, we color it using classes so we can have
  // hover / active states
  const clickableClass = clickable
    ? cx(styles.dueDateBadge, {
        [styles.isPastDue]: !isComplete && isPastDue,
        [styles.isDueNow]: !isComplete && isDueNow,
        [styles.isDueSoon]: !isComplete && isDueSoon,
        [styles.isComplete]: isComplete,
      })
    : undefined;

  // If the badge isn't clickable we can use the bgColor/fontColor props to
  // set a fixed color
  const bgColor = clickable
    ? undefined
    : isComplete
    ? Green500
    : isDueSoon
    ? Yellow500
    : isDueNow
    ? Red500
    : isPastDue
    ? Red300
    : 'transparent';

  const fontColor = clickable
    ? undefined
    : isComplete || isDueSoon || isDueNow || isPastDue
    ? N0
    : N400;

  return (
    <IconBadge
      className={cx(clickableClass, className)}
      bgColor={bgColor}
      fontColor={fontColor}
      icon={isComplete ? <DueDateIconComplete /> : <DueDateIcon />}
    >
      {children}
    </IconBadge>
  );
};

/**
 * Notification Badge
 */
interface NotificationBadgeProps {
  className?: string;
  numUnread?: number;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  numUnread = 0,
  className,
}) => (
  <IconBadge
    className={className}
    bgColor={Red500}
    fontColor={N0}
    icon={<NotificationIcon />}
  >
    {numUnread}
  </IconBadge>
);

/**
 * Subscribed Badge
 */
interface SubscribedBadgeProps {
  className?: string;
}

export const SubscribedBadge: React.FC<SubscribedBadgeProps> = ({
  className,
}) => <IconBadge className={className} icon={<SubscribeIcon />} />;

/**
 * Trello Attachments Badge
 */
interface TrelloAttachmentsBadgeProps {
  className?: string;
  numAttachments?: number;
}

export const TrelloAttachmentsBadge: React.FC<TrelloAttachmentsBadgeProps> = ({
  numAttachments = 0,
  className,
}) => (
  <IconBadge className={className} icon={<TrelloAttachmentIcon />}>
    {numAttachments}
  </IconBadge>
);

/**
 * Votes Badge
 */
interface VotesBadgeProps {
  className?: string;
  numVotes?: number;
  voted?: boolean;
}

export const VotesBadge: React.FC<VotesBadgeProps> = ({
  className,
  numVotes = 0,
  voted = false,
}) => (
  <IconBadge
    className={className}
    bgColor={voted ? N40A : 'transparent'}
    fontColor={N100}
    icon={<VoteIcon />}
  >
    {numVotes}
  </IconBadge>
);
